import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { guests, guestEndpoints } from './endpoints.js';
import { guestPii, type GuestDetail, type GuestPii } from './schemas.js';

const DEFAULT_PII_REASON = 'Admin guest profile review';

/**
 * The masked directory record for a guest.
 */
export function useGuest(guestId: string): UseQueryResult<GuestDetail> {
  return guests.useItem(guestId);
}

/**
 * The plaintext PII for a guest, returned only after an audited reason.
 *
 * The reason is fixed in this portal path because the PII shown here is the
 * minimum an operator needs to do the job; a separate flow asks for a reason
 * before a wider export.
 */
export function useGuestPii(
  guestId: string,
  reason = DEFAULT_PII_REASON,
): UseQueryResult<GuestPii> {
  const piiReason = reason.trim();

  return useApiQuery({
    queryKey: [...queryKeys.guests.detail(guestId), 'pii'],
    path: `${guestEndpoints.pii(guestId)}?reason=${encodeURIComponent(piiReason)}`,
    schema: guestPii,
    enabled: guestId !== '' && piiReason !== '',
  });
}

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
  meta: z.record(z.string(), z.string().nullable()).optional(),
});

const countryOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    code: option.value,
    name: option.label,
    phonePrefix: option.meta?.phonePrefix ?? null,
  })),
);

const codeNameOptions = z
  .array(optionSchema)
  .transform((options) => options.map((option) => ({ code: option.value, name: option.label })));

export type CountryOption = z.infer<typeof countryOptions>[number];
export type NamedCodeOption = z.infer<typeof codeNameOptions>[number];

export function useCountries(q = ''): UseQueryResult<readonly CountryOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.countries, q],
    path: guestEndpoints.countries,
    query: { q },
    schema: countryOptions,
  });
}

export function useStates(countryCode: string, q = ''): UseQueryResult<readonly NamedCodeOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.states(countryCode), q],
    path: guestEndpoints.states(countryCode),
    query: { q },
    schema: codeNameOptions,
    enabled: countryCode !== '',
  });
}

export function useCities(
  countryCode: string,
  stateCode: string,
  q = '',
): UseQueryResult<readonly NamedCodeOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.cities(countryCode, stateCode), q],
    path: guestEndpoints.cities(countryCode, stateCode),
    query: { q },
    schema: codeNameOptions,
    enabled: countryCode !== '' && stateCode !== '',
  });
}
